import React, { useEffect, useState } from 'react';
import { apiService } from '@/lib/api-functions/api-service';
import { VersionCard } from '@/components/blueprint-version/VersionCard';
import { GlossaryVersion } from '@/lib/stores/storeTypes';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirmation } from '@/hooks/use-confirmation';
import { useToast } from '@/hooks/use-toast';
import { formatVersion } from '@/lib/utils/versionUtils';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface GlossaryVersionHistoryPanelProps {
  glossaryId: string;
  majorVersion?: number;
  onRestore?: () => void; // Callback when restore is successful
  currentVersion?: { major: number; minor: number; patch: number };
}

export function GlossaryVersionHistoryPanel({ 
  glossaryId, 
  majorVersion, 
  onRestore, 
  currentVersion 
}: GlossaryVersionHistoryPanelProps) {
  const { t } = usePlatformTranslation();
  const [versions, setVersions] = useState<GlossaryVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const limit = 20;
  const { confirmation } = useConfirmation();
  const { toast } = useToast();

  const fetchVersions = async (append: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.fetchGlossaryVersions(
        glossaryId,
        majorVersion,
        limit,
        append ? offset : 0
      );

      if (response.success && response.data) {
        const newVersions = response.data;
        setVersions(append ? [...versions, ...newVersions] : newVersions);
        setHasMore(newVersions.length === limit);
        setOffset(append ? offset + newVersions.length : newVersions.length);
      } else {
        setError(response.error || 'Failed to load version history');
      }
    } catch (err) {
      setError('Failed to load version history');
      console.error('Version fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (glossaryId) {
      fetchVersions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glossaryId, majorVersion]);

  const handleLoadMore = () => {
    fetchVersions(true);
  };

  const handleRestore = async (version: GlossaryVersion) => {
    const versionString = formatVersion({
      major: version.major_version,
      minor: version.minor_version,
      patch: version.patch_version
    });

    // Check if this is a template upgrade version
    const isTemplateUpgrade = version.change_summary?.includes('Template upgraded');
    
    const confirmDialog = confirmation({
      title: 'Restore Library Object Version',
      description: isTemplateUpgrade 
        ? `⚠️ Warning: This version includes a template upgrade. Restoring may result in data structure changes. Are you sure you want to restore to v${versionString}? This will create a new version with the restored data.`
        : `Are you sure you want to restore this library object to v${versionString}? This will create a new version with the restored data. Your current version will be preserved in history.`,
      confirmText: 'Restore',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        setRestoringId(version.id);
        
        try {
          const response = await apiService.restoreGlossaryVersion(glossaryId, version.id);
          
          if (response.success) {
            toast({
              title: 'Success',
              description: `Library object restored to v${versionString}`,
            });
            
            // Refresh version history
            await fetchVersions(false);
            
            // Call parent callback to refresh the item
            onRestore?.();
            
            confirmDialog.dismiss();
          } else {
            throw new Error(response.error || 'Failed to restore version');
          }
        } catch (err) {
          console.error('Restore error:', err);
          toast({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Failed to restore version',
            variant: 'destructive',
          });
        } finally {
          setRestoringId(null);
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  };

  if (loading && versions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-sm text-gray-600">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchVersions(false)}
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-gray-500">{t('platform.object-version.objectversionhistorypanel.no_version_history_available')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('platform.object-version.objectversionhistorypanel.versions_will_appear_after_you_save_changes_to_this_item')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {versions.map((version, index) => (
        <VersionCard
          key={version.id}
          version={version as any}
          previousVersion={index < versions.length - 1 ? (versions[index + 1] as any) : undefined}
          onRestore={handleRestore as any}
          isRestoring={restoringId === version.id}
          currentVersion={currentVersion}
        />
      ))}

      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

